import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTitle } from '../context/TitleContext';
import { useAuth } from '../context/AuthContext';
import { loadTemplates, DocumentTemplate } from '../models/templateModels';
import { ArrowLeft, Send, Plus, X, Check, Mail } from 'lucide-react';

const SendDocumentRequest = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setTitle } = useTitle();
  const { currentUser } = useAuth();
  const [template, setTemplate] = useState<DocumentTemplate | null>(null);
  const [emailInput, setEmailInput] = useState('');
  const [recipientEmails, setRecipientEmails] = useState<string[]>([]);
  const [emailError, setEmailError] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);

  // Constants for timeouts
  const SEND_DELAY_MS = 1500;
  const SUCCESS_DISPLAY_MS = 2000;

  useEffect(() => {
    setTitle(t('sendDocumentRequest'));
    document.title = t('sendDocumentRequest');
  }, [setTitle, t]);

  useEffect(() => {
    if (templateId) {
      const templates = loadTemplates();
      const foundTemplate = templates.find(t => t.id === templateId);
      if (foundTemplate) {
        setTemplate(foundTemplate);
      } else {
        navigate('/templates');
      }
    }
  }, [templateId, navigate]);

  const validateEmail = (email: string): boolean => {
    // More robust email validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const handleAddEmail = () => {
    const trimmedEmail = emailInput.trim();
    
    if (!trimmedEmail) {
      setEmailError(t('emailRequired'));
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setEmailError(t('invalidEmail'));
      return;
    }

    if (recipientEmails.includes(trimmedEmail)) {
      setEmailError(t('emailAlreadyAdded'));
      return;
    }

    setRecipientEmails([...recipientEmails, trimmedEmail]);
    setEmailInput('');
    setEmailError('');
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    setRecipientEmails(recipientEmails.filter(email => email !== emailToRemove));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleSendRequest = async () => {
    if (recipientEmails.length === 0) {
      setEmailError(t('noRecipientsAdded'));
      return;
    }

    setIsSending(true);
    
    // Simulate sending emails (in a real app, this would call an API)
    await new Promise(resolve => setTimeout(resolve, SEND_DELAY_MS));
    
    // Save sent requests to localStorage for activity tracking
    const sentRequests = JSON.parse(localStorage.getItem('sentRequests') || '[]');
    const now = new Date().toISOString();
    
    recipientEmails.forEach(email => {
      sentRequests.push({
        id: `${Date.now()}-${Math.random()}`,
        templateName: template?.name || 'Unknown Template',
        templateId: template?.id, // Include template ID for navigation
        recipientEmail: email,
        sentAt: now,
        status: 'sent',
        documentCount: template?.items?.length || 0,
        actionType: 'sent', // Mark as initial send
        sentBy: currentUser?.username || t('unknown') // Use username, not display name
      });
    });
    
    localStorage.setItem('sentRequests', JSON.stringify(sentRequests));
    
    setIsSending(false);
    setSendSuccess(true);

    // Show success message then navigate back to new request page
    setTimeout(() => {
      navigate('/requests/new');
    }, SUCCESS_DISPLAY_MS);
  };

  if (!template) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const requiredDocs = template.items.filter(item => item.isRequired);
  const optionalDocs = template.items.filter(item => !item.isRequired);

  if (sendSuccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <div className="bg-green-50 dark:bg-green-900/20 rounded-full p-6 mb-6">
          <Check className="h-16 w-16 text-green-600 dark:text-green-400" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {t('requestSentSuccessfully')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 text-center max-w-md">
          {t('requestSentToRecipients', { count: recipientEmails.length })}
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <Link
          to="/templates"
          className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('backToTemplates')}
        </Link>
        
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          {t('sendDocumentRequest')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('sendRequestDescription')}
        </p>
      </div>

      {/* Template Overview */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 mb-6 border border-blue-200 dark:border-blue-800">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {template.name}
        </h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          {template.description}
        </p>
        <div className="flex items-center space-x-4 text-sm">
          <span className="px-3 py-1 bg-blue-100 dark:bg-blue-800 rounded-full text-blue-800 dark:text-blue-200">
            {requiredDocs.length} {t('required')}
          </span>
          <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-gray-800 dark:text-gray-200">
            {optionalDocs.length} {t('optional')}
          </span>
        </div>
      </div>

      {/* Document List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6 border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('documentsToRequest')}
        </h3>

        {/* Required Documents */}
        {requiredDocs.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase">
              {t('requiredDocuments')}
            </h4>
            <div className="space-y-2">
              {requiredDocs.map((item, index) => (
                <div
                  key={item.id}
                  className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                >
                  <div className="flex-shrink-0 mt-0.5">
                    <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-white">
                      {index + 1}. {item.customName || t(item.nameKey)}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {item.customDescription || t(item.descriptionKey)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Optional Documents */}
        {optionalDocs.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase">
              {t('optionalDocuments')}
            </h4>
            <div className="space-y-2">
              {optionalDocs.map((item, index) => (
                <div
                  key={item.id}
                  className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                >
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="h-5 w-5 rounded border-2 border-gray-400 dark:border-gray-500" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-white">
                      {index + 1}. {item.customName || t(item.nameKey)}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {item.customDescription || t(item.descriptionKey)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Email Recipients Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <Mail className="h-5 w-5 mr-2" />
          {t('recipientEmails')}
        </h3>

        {/* Email Input */}
        <div className="mb-4">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('addRecipientEmail')}
          </label>
          <div className="flex space-x-2">
            <input
              id="email"
              type="email"
              value={emailInput}
              onChange={(e) => {
                setEmailInput(e.target.value);
                setEmailError('');
              }}
              onKeyPress={handleKeyPress}
              placeholder={t('enterEmailAddress')}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              onClick={handleAddEmail}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center"
            >
              <Plus className="h-5 w-5 mr-1" />
              {t('add')}
            </button>
          </div>
          {emailError && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">{emailError}</p>
          )}
        </div>

        {/* Email List */}
        {recipientEmails.length > 0 && (
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('recipients')} ({recipientEmails.length})
            </p>
            <div className="space-y-2">
              {recipientEmails.map((email) => (
                <div
                  key={email}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                >
                  <span className="text-gray-900 dark:text-white">{email}</span>
                  <button
                    onClick={() => handleRemoveEmail(email)}
                    className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 p-1"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Send Button */}
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleSendRequest}
            disabled={isSending || recipientEmails.length === 0}
            className={`w-full flex items-center justify-center px-6 py-3 rounded-md text-white font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              isSending || recipientEmails.length === 0
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {isSending ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                {t('sendingRequest')}
              </>
            ) : (
              <>
                <Send className="h-5 w-5 mr-2" />
                {t('sendRequest')} {recipientEmails.length > 0 && `(${recipientEmails.length})`}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendDocumentRequest;
